/**
 * CardMarket URL helpers
 *
 * Implements PRICING_SPEC.md:
 * - Set slug decision tree (OGN promos → OGNX / Origins-Promos)
 * - Card slug generation (spaces/slashes/commas → hyphens)
 * - Variant suffix decision tree (V1/V2/V3 overnumbered, runes)
 * - Optional ?idProduct query param when available
 */

function isOgnPromoId(cardId) {
  const id = String(cardId || '');
  if (!/^OGN-/i.test(id)) return false;
  return /b$/i.test(id) || /-PROMO$/i.test(id);
}

function getSetCodeFromCard(card) {
  const explicit = card?.set?.value?.id;
  if (explicit && typeof explicit === 'string') {
    return explicit.toUpperCase();
  }
  const rawId = String(card?.id || '');
  const parts = rawId.split('-');
  if (!parts.length) return '';
  return parts[0].toUpperCase();
}

function getSetSlugForCard(card) {
  const rawSet = getSetCodeFromCard(card);
  if (!rawSet) return 'Riftbound';

  const cardId = String(card?.id || '');
  const effectiveSet =
    rawSet === 'OGN' && isOgnPromoId(cardId) ? 'OGNX' : rawSet;

  if (effectiveSet === 'OGN') return 'Origins';
  if (effectiveSet === 'OGS') return 'Proving-Grounds';
  if (effectiveSet === 'SFD') return 'Spiritforged';
  if (effectiveSet === 'OGNX') return 'Origins-Promos';
  return effectiveSet;
}

// Card slug algorithm (PRICING_SPEC "URL Slug Generation"):
// 1. Trim
// 2. Replace spaces, slashes, commas with hyphens
// 3. Collapse multiple hyphens
// 4. Trim leading/trailing hyphens
// 5. URL-encode result (handled in buildCardmarketUrl)
function buildCardSlug(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const replaced = raw.replace(/[ ,/]+/g, '-');
  const collapsed = replaced.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  return collapsed;
}

function isRuneCard(card) {
  const typeField = card?.cardType?.type;
  const parts = [];
  if (Array.isArray(typeField)) {
    for (const t of typeField) {
      if (!t) continue;
      if (t.label) parts.push(String(t.label));
      if (t.id) parts.push(String(t.id));
    }
  } else if (typeField && typeof typeField === 'object') {
    if (typeField.label) parts.push(String(typeField.label));
    if (typeField.id) parts.push(String(typeField.id));
  } else if (typeField) {
    parts.push(String(typeField));
  }
  const combined = parts.join(' ').toLowerCase();
  return combined.includes('rune');
}

function getVariantSuffix(card) {
  const id = String(card?.id || '');
  const rarityLabel = String(card?.rarity?.value?.label || '');

  // Decision tree from PRICING_SPEC "Variant Suffix Decision Tree"
  if (/\*$/.test(id)) {
    return '-V3-Overnumbered'; // signed
  }
  if (/a(-\d*)?$/i.test(id)) {
    return '-V2-Overnumbered'; // showcase/alt
  }
  if (isRuneCard(card) && rarityLabel === 'Common') {
    return '-V1-Common'; // common rune
  }
  if (/Showcase/i.test(rarityLabel)) {
    return '-V2-Overnumbered'; // showcase
  }
  return '';
}

export function buildCardmarketUrl(card) {
  if (!card) return null;

  const nameForSlug = (card.cardmarketName || card.name || '').trim();
  if (!nameForSlug) return null;

  const base = 'https://www.cardmarket.com/en/Riftbound/Products/Singles/';
  const setSlug = getSetSlugForCard(card);
  const cardSlug = buildCardSlug(nameForSlug);

  // Fallback: simple search query if slug is empty
  if (!cardSlug) {
    const search = encodeURIComponent(nameForSlug);
    return `${base}?search=${search}`;
  }

  const variantSuffix = getVariantSuffix(card);
  const idProduct = card.idProduct || card.price?.idProduct;
  const path = `${encodeURIComponent(setSlug)}/${encodeURIComponent(
    cardSlug
  )}${variantSuffix}`;

  if (idProduct != null) {
    return `${base}${path}?idProduct=${encodeURIComponent(String(idProduct))}`;
  }

  return `${base}${path}`;
}

